import firebase from '@firebase/app'
import * as firestore from '@firebase/firestore-types'
import * as shortid from 'shortid'
import Vue from 'vue'
import { getModule, Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'

import { fs, RootLevelCollection } from '@/datastore'

import { Brand, defaultBrand } from '@/datastore/models'
import { Category, defaultCategory } from '@/datastore/models'
import { Coupon, defaultCoupon } from '@/datastore/models'
import { Household, defaultHousehold } from '@/datastore/models'
import { Invite } from '@/datastore/models'
import { List, defaultList } from '@/datastore/models'
import { PantryItem, defaultPantryItem } from '@/datastore/models'
import { ShoppingListItem, defaultShoppingListItem } from '@/datastore/models'
import { Variation } from '@/datastore/models'

import { Brand_PantryItem, defaultBrand_PantryItem } from '@/datastore/models'
import { Category_List, defaultCategory_List } from '@/datastore/models'
import { Category_PantryItem, defaultCategory_PantryItem } from '@/datastore/models'
import { Household_User, defaultHousehold_User } from '@/datastore/models'
import { PantryItem_Variation } from '@/datastore/models'

import { V1 } from '@/export'

import store from '@/store'
import auth from '@/store/modules/auth'

export const groceryInitialization = new Promise<void>(resolve => {
	const unsubscribe = store.subscribeAction({
		after(action, state) {
			if (state.grocery.initialized) {
				resolve()
				unsubscribe()
			}
		},
	})
})

@Module({ dynamic: true, name: 'grocery', namespaced: true, store })
class GroceryModule extends VuexModule implements GroceryStore {

	initialized = false

	brandsCollection: Dictionary<Brand> = {}
	categoriesCollection: Dictionary<Category> = {}
	couponsCollection: Dictionary<Coupon> = {}
	householdCollection: Dictionary<Household> = {}
	listsCollection: Dictionary<List> = {}
	pantryItemsCollection: Dictionary<PantryItem> = {}
	shoppingListItemsCollection: Dictionary<ShoppingListItem> = {}
	variationsCollection: Dictionary<Variation> = {}

	brands_pantryItemsCollection: Dictionary<Brand_PantryItem> = {}
	categories_pantryItemsCollection: Dictionary<Category_PantryItem> = {}
	categories_listsCollection: Dictionary<Category_List> = {}
	households_usersCollection: Dictionary<Household_User> = {}
	pantryItems_variationsCollection: Dictionary<PantryItem_Variation> = {}

	get brandPantryItems() {
		const { brandsCollection, brands_pantryItemsCollection } = this

		const brandPantryItems: Dictionary<string[] | undefined> = {}

		for (const brandId in brandsCollection) {
			brandPantryItems[brandId] = Object.keys(brands_pantryItemsCollection)
				.filter(r => r.startsWith(brandId))
				.map(r => brands_pantryItemsCollection[r])
				.filter<Brand_PantryItem>((i): i is Brand_PantryItem => !!i)
				.map(i => i.pantryItemId)
		}

		return brandPantryItems
	}

	get brands() {
		return Object.values(this.brandsCollection)
			.sort((a, b) => a.name.localeCompare(b.name))
	}

	get coupons() {
		return Object.values(this.couponsCollection)
			.filter(coupon => (coupon.expirationDate ?? Number.MAX_SAFE_INTEGER) > Date.now())
	}

	get categories() {
		return Object.values(this.categoriesCollection)
			.sort((a, b) => a.name.localeCompare(b.name))
	}

	get categoryLists() {
		const { categoriesCollection, categories_listsCollection } = this

		const categoryLists: Dictionary<string[] | undefined> = {}

		for (const categoryId in categoriesCollection) {
			categoryLists[categoryId] = Object.keys(categories_listsCollection)
				.filter(r => r.startsWith(categoryId))
				.map(r => categories_listsCollection[r])
				.filter<Category_List>((i): i is Category_List => !!i)
				.map(i => i.listId)
		}

		return categoryLists
	}

	get categoryPantryItems() {
		const { categoriesCollection, categories_pantryItemsCollection } = this

		const categoryPantryItems: Dictionary<string[] | undefined> = {}

		for (const categoryId in categoriesCollection) {
			categoryPantryItems[categoryId] = Object.keys(categories_pantryItemsCollection)
				.filter(r => r.startsWith(categoryId))
				.map(r => categories_pantryItemsCollection[r])
				.filter<Category_PantryItem>((i): i is Category_PantryItem => !!i)
				.map(i => i.pantryItemId)
		}

		return categoryPantryItems
	}

	get expiredCoupons() {
		return Object.values(this.couponsCollection)
			.filter(coupon => (coupon.expirationDate ?? Number.MAX_SAFE_INTEGER) <= Date.now())
	}

	get households() {
		return Object.values(this.householdCollection)
			.sort((a, b) => a.name.localeCompare(b.name))
	}

	get householdMembers() {
		const { households_usersCollection, householdCollection } = this

		const householdMembers: Dictionary<Household_User[] | undefined> = {}

		for (const householdId in householdCollection) {
			householdMembers[householdId] = Object.keys(households_usersCollection)
				.filter(r => r.startsWith(householdId))
				.map(r => households_usersCollection[r])
				.filter<Household_User>((i): i is Household_User => !!i)
		}

		return householdMembers
	}

	get listCategories() {
		const { categories_listsCollection, listsCollection } = this

		const listCategories: Dictionary<string[] | undefined> = {}

		for (const listId in listsCollection) {
			listCategories[listId] = Object.keys(categories_listsCollection)
				.filter(r => r.endsWith(listId))
				.map(r => categories_listsCollection[r])
				.filter<Category_List>((i): i is Category_List => !!i)
				.map(i => i.categoryId)
		}

		return listCategories
	}

	get lists() {
		return Object.values(this.listsCollection)
			.sort((a, b) => a.name.localeCompare(b.name))
	}

	get pantryItemBrands() {
		const { brands_pantryItemsCollection, pantryItemsCollection } = this

		const brandPantryItems = Object.entries(brands_pantryItemsCollection)

		return Object.keys(pantryItemsCollection).reduce((map, pantryItemId) => {
			map[pantryItemId] = brandPantryItems
				.filter<[string, Brand_PantryItem]>((r): r is [string, Brand_PantryItem] => !!r[1] && r[0].endsWith(pantryItemId))
				.map(r => r[1].brandId)
			return map
		}, {} as Dictionary<string[]>)
	}

	get pantryItemCategories() {
		const { categories_pantryItemsCollection, pantryItemsCollection } = this

		const categoryPantryItems = Object.entries(categories_pantryItemsCollection)

		return Object.keys(pantryItemsCollection).reduce((map, pantryItemId) => {
			map[pantryItemId] = categoryPantryItems
				.filter<[string, Category_PantryItem]>((r): r is [string, Category_PantryItem] => !!r[1] && r[0].endsWith(pantryItemId))
				.map(r => r[1].categoryId)
			return map
		}, {} as Dictionary<string[]>)
	}

	get pantryItems() {
		return Object.values(this.pantryItemsCollection)
			.sort((a, b) => a.name.localeCompare(b.name))
	}

	get shoppingListItems() {
		return Object.values(this.shoppingListItemsCollection)
	}

	get uncategorizedPantryItems() {
		const { categories_pantryItemsCollection } = this
		const categoryPantryItemRelationships = Object.keys(categories_pantryItemsCollection)

		return this.pantryItems
			.filter((i: PantryItem) => !categoryPantryItemRelationships.some(id => id.endsWith(i.$id)))
	}

	@Action
	async addBrand(name: string) {
		return fs.collection('brands').add({ name })
	}

	@Action
	async addCategory(name: string) {
		return fs.collection('categories').add({ name })
	}

	@Action
	async addCoupon(options: CreateCouponOptions) {
		return fs.collection('coupons').add(options)
	}

	@Action
	async addList(name: string) {
		return fs.collection('lists').add({ name })
	}

	@Action
	async addPantryItem(name: string) {
		return fs.collection('pantry-items').add({ name })
	}

	@Action
	async addPantryItemToBrand({ batch, brandId, pantryItemId }: UpdateBrandPantryItemRelationshipOptions) {

		await createRelationship<Brand_PantryItem>({
			batch,
			collection: '(brands+pantry-items)',
			ids: { brandId, pantryItemId },
		})
	}

	@Action
	async addPantryItemToCategory({ batch, categoryId, pantryItemId }: UpdateCategoryPantryItemRelationshipOptions) {

		await createRelationship<Category_PantryItem>({
			batch,
			collection: '(categories+pantry-items)',
			ids: { categoryId, pantryItemId },
		})
	}

	@Action
	async addShoppingListItem(options: CreateShoppingListItemOptions) {
		return fs.collection('shopping-list-items').add(options)
	}

	@Action
	async createHousehold(name: string) {
		const { user } = auth

		if (!user) {
			return
		}

		const doc = await fs.collection('households').add({ name })

		const householdId = doc.id
		const userId = user.uid

		await createRelationship<Household_User>({
			collection: '(households+users)',
			ids: { householdId, userId },
			supplementalData: {
				displayName: user.displayName || '',
				isAdmin: true,
				photoURL: user.photoURL || '',
			},
		})
	}

	@Action
	async createInviteCode(householdId: string): Promise<string> {

		const id = shortid.generate()

		await fs.collection('invites')
			.doc(id)
			.set({
				creationDate: firebase.firestore!.Timestamp.fromDate(new Date()),
				householdId,
			})

		return id
	}

	@Action
	async deleteBrand(brandId: string) {
		const relationshipDocs = await getRelationshipDocs(['(brands+pantry-items)'], 'brandId', '==', brandId)
		const docs = [...relationshipDocs, fs.collection('brands').doc(brandId)]

		const batch = fs.batch()
		docs.forEach(doc => batch.delete(doc))
		await batch.commit()
	}

	@Action
	async deleteCategory(categoryId: string) {
		const relationshipDocs = await getRelationshipDocs(['(categories+lists)', '(categories+pantry-items)'], 'categoryId', '==', categoryId)
		const docs = [...relationshipDocs, fs.collection('categories').doc(categoryId)]

		const batch = fs.batch()
		docs.forEach(doc => batch.delete(doc))
		await batch.commit()
	}

	@Action
	async deleteCoupon(couponId: string) {
		await fs.collection('coupons').doc(couponId).delete()
	}

	@Action
	async deleteList(listId: string) {
		const relationshipDocs = await getRelationshipDocs(['(categories+lists)'], 'listId', '==', listId)
		const docs = [...relationshipDocs, fs.collection('lists').doc(listId)]

		const batch = fs.batch()
		docs.forEach(doc => batch.delete(doc))
		await batch.commit()
	}

	@Action
	async deletePantryItem(pantryItemId: string) {
		const relationshipDocs = await getRelationshipDocs(['(categories+pantry-items)'], 'pantryItemId', '==', pantryItemId)
		const docs = [...relationshipDocs, fs.collection('pantry-items').doc(pantryItemId)]

		const batch = fs.batch()
		docs.forEach(doc => batch.delete(doc))
		await batch.commit()
	}

	@Action
	async deleteShoppingListItem(id: string) {
		await fs.collection('shopping-list-items').doc(id).delete()
	}

	/**
	 * Imports pantry items into a household.
	 *
	 * @param householdId
	 * 		the id of the household to import the data into
	 * @param data
	 * 		the data to import
	 */
	@Action({ rawError: true })
	async import({ pantryData, householdId }: { pantryData: V1.Schema, householdId: string }) {

		const householdRef = fs.collection('households').doc(householdId)

		const brands_pantryItemsRef = householdRef.collection('(brands+pantry-items)')
		const categories_listsRef = householdRef.collection('(categories+lists)')
		const categories_pantryItemsRef = householdRef.collection('(categories+pantry-items)')

		const brandsRef = householdRef.collection('brands')
		const categoriesRef = householdRef.collection('categories')
		const listsRef = householdRef.collection('lists')
		const pantryItemsRef = householdRef.collection('pantry-items')
		const shoppingListItemsRef = householdRef.collection('shopping-list-items')

		const queue: [firestore.DocumentReference, firestore.DocumentData][] = []

		for (const [brandId, pantryItemId] of pantryData['(brands+pantry-items)']) {
			const relationship = { brandId, pantryItemId }
			const $id = toId(relationship)
			queue.push([brands_pantryItemsRef.doc($id), relationship])
		}

		for (const [categoryId, listId] of pantryData['(categories+lists)']) {
			const relationship = { categoryId, listId }
			const $id = toId(relationship)
			queue.push([categories_listsRef.doc($id), relationship])
		}

		for (const [categoryId, pantryItemId] of pantryData['(categories+pantry-items)']) {
			const relationship = { categoryId, pantryItemId }
			const $id = toId(relationship)
			queue.push([categories_pantryItemsRef.doc($id), relationship])
		}

		for (const [$id, brand] of Object.entries(pantryData['brands'])) {
			queue.push([brandsRef.doc($id), brand])
		}

		for (const [$id, category] of Object.entries(pantryData['categories'])) {
			queue.push([categoriesRef.doc($id), category])
		}

		for (const [$id, list] of Object.entries(pantryData['lists'])) {
			queue.push([listsRef.doc($id), list])
		}

		for (const [$id, pantryItem] of Object.entries(pantryData['pantry-items'])) {
			queue.push([pantryItemsRef.doc($id), pantryItem])
		}

		for (const [$id, shoppingListItem] of Object.entries(pantryData['shopping-list-items'])) {
			queue.push([shoppingListItemsRef.doc($id), shoppingListItem])
		}

		while (queue.length > 0) {
			const batch = fs.batch()

			for (let i = 0; i < 500; i++) {
				const args = queue.shift()
				if (!args) {
					break
				}
				batch.set.apply(batch, args)
			}

			await batch.commit()
		}
	}

	@Action
	async incrementShoppingListItemQuantity({ amount, id }: { amount: number, id: string }) {
		await fs.collection('shopping-list-items').doc(id).update({
			quantity: firebase.firestore!.FieldValue.increment(amount),
		})
	}

	@Action({ commit: 'set' })
	async initialize() {
		if (process.env.NODE_ENV !== 'production') {
			await fs.disableNetwork()
		}

		const [
			brandsCollection,
			categoriesCollection,
			couponsCollection,
			householdCollection,
			listsCollection,
			pantryItemsCollection,
			shoppingListItemsCollection,
		] = await Promise.all([
			syncCollection(this, 'brands', 'brandsCollection', defaultBrand),
			syncCollection(this, 'categories', 'categoriesCollection', defaultCategory),
			syncCollection(this, 'coupons', 'couponsCollection', defaultCoupon),
			syncCollection(this, 'households', 'householdCollection', defaultHousehold),
			syncCollection(this, 'lists', 'listsCollection', defaultList),
			syncCollection(this, 'pantry-items', 'pantryItemsCollection', defaultPantryItem),
			syncCollection(this, 'shopping-list-items', 'shoppingListItemsCollection', defaultShoppingListItem),
		])

		const [
			brands_pantryItemsCollection,
			categories_pantryItemsCollection,
			categories_listsCollection,
			households_usersCollection,
		] = await Promise.all([
			syncCollection(this, '(brands+pantry-items)', 'brands_pantryItemsCollection', defaultBrand_PantryItem),
			syncCollection(this, '(categories+pantry-items)', 'categories_pantryItemsCollection', defaultCategory_PantryItem),
			syncCollection(this, '(categories+lists)', 'categories_listsCollection', defaultCategory_List),
			syncCollection(this, '(households+users)', 'households_usersCollection', defaultHousehold_User),
		])

		return {
			initialized: true,
			brandsCollection,
			categoriesCollection,
			couponsCollection,
			householdCollection,
			listsCollection,
			pantryItemsCollection,
			shoppingListItemsCollection,
			brands_pantryItemsCollection,
			categories_pantryItemsCollection,
			categories_listsCollection,
			households_usersCollection,
		}
	}

	@Action({ rawError: true })
	async joinHousehold(inviteCode: string): Promise<void> {

		const { user } = auth
		if (!user) {
			throw new NotLoggedInError()
		}

		const inviteRef = await fs.collection('invites').doc(inviteCode).get()
		const invite = inviteRef.data() as Invite | undefined
		if (!invite) {
			throw new InviteNotFoundError()
		}

		const householdId = invite.householdId
		const userId = user.uid

		await createRelationship<Household_User>({
			collection: '(households+users)',
			ids: { householdId, userId },
			supplementalData: {
				displayName: user.displayName || '',
				isAdmin: false,
				photoURL: user.photoURL || '',
			},
		})
	}

	@Action
	async removePantryItemFromBrand({ batch, brandId, pantryItemId }: UpdateBrandPantryItemRelationshipOptions) {
		return deleteRelationship('(brands+pantry-items)', { brandId, pantryItemId }, batch)
	}

	@Action
	async removePantryItemFromCategory(options: UpdateCategoryPantryItemRelationshipOptions) {
		const { batch, categoryId, pantryItemId } = options
		return deleteRelationship('(categories+pantry-items)', { categoryId, pantryItemId }, batch)
	}

	@Action
	async updateCategory(options: UpdateCategoryOptions) {
		const { categoryId, color, icon, name, lists, pantryItems } = options

		const listsDiff = diff({
			curr: lists,
			prev: this.categoryLists[categoryId] || [],
		})

		const pantryItemsDiff = diff({
			curr: pantryItems,
			prev: this.categoryPantryItems[categoryId] || [],
		})

		const batch = fs.batch()

		for (const listId of listsDiff.additions) {
			await createRelationship<Category_List>({
				batch,
				collection: '(categories+lists)',
				ids: { categoryId, listId },
			})
		}

		for (const listId of listsDiff.deletions) {
			await deleteRelationship('(categories+lists)', { categoryId, listId }, batch)
		}

		for (const pantryItemId of pantryItemsDiff.additions) {
			await this.addPantryItemToCategory({ batch, categoryId, pantryItemId })
		}

		for (const pantryItemId of pantryItemsDiff.deletions) {
			await this.removePantryItemFromCategory({ batch, categoryId, pantryItemId })
		}

		batch.set(fs.collection('categories').doc(categoryId), <Category>{ name, color, icon })

		await batch.commit()
	}

	@Action
	async updateCoupon(options: UpdateCouponOptions) {
		const { couponId, description, promoItems, title } = options
		const expirationDate = options.expirationDate ?? null

		const batch = fs.batch()

		batch.set(fs.collection('coupons').doc(couponId), <Coupon>{ description, expirationDate, promoItems, title })

		await batch.commit()
	}

	@Action
	async updateList(options: { categoryIds?: string[], listId: string, name?: string }) {
		const { categoryIds, listId, name } = options

		const batch = fs.batch()

		if (categoryIds) {
			const categoriesDiff = diff({
				curr: categoryIds,
				prev: this.listCategories[listId] || [],
			})

			for (const categoryId of categoriesDiff.additions) {
				await createRelationship<Category_List>({
					batch,
					collection: '(categories+lists)',
					ids: { categoryId, listId },
				})
			}

			for (const categoryId of categoriesDiff.deletions) {
				await deleteRelationship('(categories+lists)', { categoryId, listId }, batch)
			}
		}

		if (name) {
			batch.set(fs.collection('lists').doc(listId), <List>{ name })
		}

		await batch.commit()
	}

	@Action
	async updatePantryItem(options: UpdatePantryItemOptions) {
		const { brands, categories, name, notes, pantryItemId } = options

		const brandsDiff = diff({
			curr: brands,
			prev: this.pantryItemBrands[pantryItemId],
		})

		const categoriesDiff = diff({
			curr: categories,
			prev: this.pantryItemCategories[pantryItemId],
		})

		const batch = fs.batch()

		for (const brandId of brandsDiff.additions) {
			await this.addPantryItemToBrand({ batch, brandId, pantryItemId })
		}

		for (const brandId of brandsDiff.deletions) {
			await this.removePantryItemFromBrand({ batch, brandId, pantryItemId })
		}

		for (const categoryId of categoriesDiff.additions) {
			await this.addPantryItemToCategory({ batch, categoryId, pantryItemId })
		}

		for (const categoryId of categoriesDiff.deletions) {
			await this.removePantryItemFromCategory({ batch, categoryId, pantryItemId })
		}

		batch.set(fs.collection('pantry-items').doc(pantryItemId), <PantryItem>{ name, notes })

		await batch.commit()
	}

	@Mutation
	deleteEntity({ key, id }: { key: keyof GroceryStore, id: string }) {
		Vue.delete(this[key], id)
	}

	@Mutation
	set(payload: Partial<GroceryModule>) {
		Object.assign(this, payload)
	}

	@Mutation
	setEntity({ key, id, value }: { key: keyof GroceryStore, id: string, value: any }) {
		Vue.set(this[key], id, value)
	}

}

export default getModule(GroceryModule)

export class InviteNotFoundError extends Error {}
export class NotLoggedInError extends Error {}

async function createRelationship<T>(options: CreateRelationshipOptions<T>) {
	const { collection, ids, supplementalData } = options
	const id = toId(ids)
	const batch = options.batch || fs.batch()

	batch.set(fs.collection(collection).doc(id), { ...supplementalData, ...ids })

	if (!options.batch) {
		await batch.commit()
	}
}

async function deleteRelationship<D>(
	relationship: RootLevelCollection,
	data: D,
	batch?: firestore.WriteBatch,
) {
	const id = toId(data)
	const commit = !batch

	batch = batch || fs.batch()

	batch.delete(fs.collection(relationship).doc(id))

	if (commit) {
		batch.commit()
	}
}

function diff<T>({ prev, curr }: { prev: T[], curr: T[] }) {
	return {
		additions: curr.filter(i => !prev.includes(i)),
		deletions: prev.filter(i => !curr.includes(i)),
	}
}

async function getRelationshipDocs(
	relationships: RootLevelCollection[],
	fieldPath: string | firestore.FieldPath,
	opStr: firestore.WhereFilterOp,
	value: any,
): Promise<firestore.DocumentReference[]> {
	const snapshots = await Promise.all(relationships.map(r => fs.collection(r).where(fieldPath, opStr, value).get()))
	return snapshots.reduce<firestore.DocumentReference[]>((docs, snapshot) => docs.concat(snapshot.docs.map(d => d.ref)), [])
}

async function syncCollection<T, K extends keyof GroceryStore>(
	context: GroceryModule,
	collection: RootLevelCollection,
	prop: K,
	template: Readonly<T>,
): Promise<GroceryStore[K]> {
	let firstSync = true

	return new Promise<GroceryStore[K]>(resolve => {

		fs.collection(collection).onSnapshot(snapshot => {
			const map = { ...context[prop] }

			function createInitialState({ doc, type }: firestore.DocumentChange) {

				switch (type) {
					case 'added':
					case 'modified':
						map[doc.id as keyof GroceryModule[K]] = { ...template, ...doc.data(), $id: doc.id } as any
						break
					case 'removed':
						delete map[doc.id]
						break
				}
			}

			function updateCurrentState({ doc, type }: firestore.DocumentChange) {

				switch (type) {
					case 'added':
					case 'modified':
						context.setEntity({
							id: doc.id,
							key: prop,
							value: { ...template, ...doc.data(), $id: doc.id },
						})
						break
					case 'removed':
						context.deleteEntity({ id: doc.id, key: prop })
						break
				}
			}

			if (firstSync) {
				firstSync = false
				snapshot.docChanges().forEach(createInitialState)
				resolve(map)
			}
			else {
				snapshot.docChanges().forEach(updateCurrentState)
			}
		})
	})
}

function toId<T>(ids: T): string {

	return Object.entries(ids)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([key, value]) => value)
		.reduce((id, key) => `${id}+${key}`)
}

export interface GroceryStore {
	brandsCollection: Dictionary<Brand>
	categoriesCollection: Dictionary<Category>
	couponsCollection: Dictionary<Coupon>
	householdCollection: Dictionary<Household>
	listsCollection: Dictionary<List>
	pantryItemsCollection: Dictionary<PantryItem>
	shoppingListItemsCollection: Dictionary<ShoppingListItem>
	variationsCollection: Dictionary<Variation>

	brands_pantryItemsCollection: Dictionary<Brand_PantryItem>
	categories_listsCollection: Dictionary<Category_List>
	categories_pantryItemsCollection: Dictionary<Category_PantryItem>
	households_usersCollection: Dictionary<Household_User>
	pantryItems_variationsCollection: Dictionary<PantryItem_Variation>
}

interface CreateRelationshipOptions<T> {
	batch?: firestore.WriteBatch
	collection: RootLevelCollection
	ids: Partial<T>
	supplementalData?: Partial<T>
}

type CreateCouponOptions = Omit<Coupon, '$id'>

interface CreateShoppingListItemOptions {
	createdAt: number
	createdBy?: string
	name: string
	notes: string
	pantryItemId: string
	preferredBrands: string[]
	quantity: number
}

interface UpdateCategoryPantryItemRelationshipOptions {
	batch?: firestore.WriteBatch
	categoryId: string
	pantryItemId: string
}

interface UpdateCategoryOptions {
	categoryId: string
	color: string
	icon: string
	name: string
	lists: string[]
	pantryItems: string[]
}

interface UpdateCouponOptions extends CreateCouponOptions {
	couponId: string
}

interface UpdatePantryItemOptions {
	brands: string[]
	categories: string[]
	name: string
	notes: string
	pantryItemId: string
}

interface UpdateBrandPantryItemRelationshipOptions {
	batch?: firestore.WriteBatch
	brandId: string
	pantryItemId: string
}
